import _, { get, reduce } from 'lodash';
import { connectIsolatedPointsNearest, singlePoint, syncAxisPoints } from './SeriesTransform';
import { getTrendData } from '../../data-series/DataSeries';

export function getNumberPoints(chartDef, data) {
    if (!chartDef || !data) return;
    const lengths = chartDef.chartTrends
        .filter((def) => !def.hidden)
        .map((def) => {
            return _.isNil(def?.dataIndex) ? data?.trends?.[def.key] : data?.[def.dataIndex]?.trends?.[def.key];
        })
        .map((trend) => trend?.data?.length || 0);
    return Math.max(0, ...lengths);
}

const getTrendGranularity = (def, data) => {
    const trendData = getTrendData(def, data);
    if (!trendData) return 1_000;
    return trendData.granularity || 2_000;
};

export function getGranularity(chartDef, data) {
    if (!chartDef) return undefined;

    const granularities = chartDef.chartTrends
        .filter((def) => !def.hidden)
        .map((def) => getTrendGranularity(def, data));

    return Math.max(3_000, ...granularities);
}

const getSingleSeries = (chartDef, seriesDef, data, selectedDate, granularity) => {
    const metricMapping = chartDef.metricMapping || ((num) => num);
    const trendData = getTrendData(seriesDef, data);
    const statusTrendData = seriesDef.statusKey ? data?.trends?.[seriesDef.statusKey] : null;
    const yAxis = seriesDef.index || 0;

    if (!trendData) {
        return {
            color: seriesDef.color,
            name: seriesDef.label,
            data: [],
            type: seriesDef.chartType,
            yAxis,
        };
    }

    let accessor;
    if (seriesDef.value) {
        accessor = (item) => {
            const itemValue = item.value;
            return itemValue ? itemValue[seriesDef.value] : undefined;
        };
    } else {
        accessor = (item) => item.value;
    }

    const seriesData = trendData.data.map((pointData) => {
        const rawVal = accessor(pointData);
        const y = metricMapping(rawVal, granularity);
        const value = pointData.orig || y;

        const className = selectedDate === pointData.date ? 'selected-time' : undefined;

        const worst = statusTrendData?.data
            ? _.find(statusTrendData.data, (d) => d.date === pointData.date)?.value?.worst
            : undefined;

        const status = worst === 'yellow' || worst === 'orange' ? worst : undefined;

        return {
            className,
            x: pointData.date,
            y,
            value,
            def: seriesDef,
            pointData,
            data,
            chartDef,
            granularity,
            status,
        };
    });

    return {
        color: seriesDef.color,
        data: seriesData,
        name: seriesDef.label,
        type: seriesDef.chartType,
        yAxis,
    };
};

const alertStatus = (color) => {
    return color === 'red' || color === 'orange' || color === 'yellow' ? color : undefined;
};

const getStatusValues = (data, statusMetric) => {
    if (!statusMetric) return [];
    const statusData = data?.trends?.[statusMetric];
    if (!statusData) return [];
    return statusData.data.map((item) => {
        const worst = item.value?.worst;
        return {
            x: item.date,
            status: alertStatus(worst),
        };
    });
};

const getTimeslice = (chartDef, series, statusValues, point, granularity) => {
    const findClosestToX = findClosestTo('x', point?.x);
    const items = findClosestToX(series.flatMap((item) => item.data));
    const statusArr = findClosestToX(statusValues).map((item) => item.status);
    const status = statusArr.length ? statusArr[0] : undefined;
    return {
        chartDef,
        items,
        status,
        point,
        x: point?.x,
        granularity,
    };
};

const transform = (chartDef, series) => {
    const nSeries = singlePoint(series);
    return connectIsolatedPointsNearest(nSeries);
};

const mouseOver = (ev, onMouseOver) => {
    const point = ev.target;
    const allSeries = point.chartDef.chartTrends.map((def) =>
        getSingleSeries(point.chartDef, def, point.data, undefined, point.granularity)
    );
    const statusValues = getStatusValues(point.data, point.chartDef.statusMetric);
    onMouseOver(true, getTimeslice(point.chartDef, allSeries, statusValues, point, point.granularity));
};

export function getSeries(chartDef, data, granularity, onMouse, handleEvents, selectedDate) {
    if (!chartDef) return undefined;

    const seriesList = chartDef.chartTrends
        .filter((seriesDef) => !seriesDef.hidden)
        .map((seriesDef) => getSingleSeries(chartDef, seriesDef, data, selectedDate, granularity))
        .map((series) => transform(chartDef, series));

    if (chartDef?.transforms?.syncAxisPoints) {
        // This transform needs to be applied to the data across all series, not on a per-series basis
        syncAxisPoints(seriesList);
    }

    return seriesList.map((item) => {
        return {
            ...item,
            point: {
                events: {
                    mouseOut: () => onMouse(false),
                    mouseOver: (ev) => mouseOver(ev, onMouse),
                    click: (ev) => {
                        if (handleEvents?.onClick) {
                            handleEvents.onClick({
                                point: ev.point,
                                pointData: ev.point?.pointData,
                                granularity: ev.point?.granularity || granularity,
                            });
                        }
                    },
                },
            },
        };
    });
}

export function findClosestTo(key, value) {
    return (items) => {
        const closest = reduce(
            items,
            (best, current) => {
                if (!best.data || Math.abs(get(current, key) - value) < best.distance) {
                    return { distance: Math.abs(get(current, key) - value), data: [current] };
                }

                if (Math.abs(get(current, key) - value) === best.distance) {
                    return { distance: best.distance, data: [...best.data, current] };
                }

                return best;
            },
            { distance: Infinity, data: undefined }
        );
        return closest.data || [];
    };
}
